import { z } from 'zod';
import {
  BaseResponseSchema,
} from './primitives.js';

import { LocationIntegrationVendorTypeSchema } from './location-integration.schemas.js';

// ========================================
// Provider Ops — Site Tag Fleet (Phase 0)
// ========================================

export const VendorPollHeartbeatStatusSchema = z.enum(['not_configured', 'active', 'stale']);


export const SiteTagFleetSummaryRowSchema = z.object({
  tenantId: z.string(),
  siteId: z.string(),
  siteName: z.string().nullable().optional(),
  tagsSeenCount: z.number().int().min(0),
  tagsOnlineCount: z.number().int().min(0),
  tagsOfflineCount: z.number().int().min(0),
  lowBatteryCount: z.number().int().min(0),
  inventoryRegisteredCount: z.number().int().min(0).nullable().optional(),
  unregisteredSeenCount: z.number().int().min(0),
  neverSeenInventoryCount: z.number().int().min(0).nullable().optional(),
  /** Cloud vendor integration poll freshness (Kontakt / Pozyx). */
  vendorPollStatus: VendorPollHeartbeatStatusSchema.optional(),
  vendorPollLastAt: z.string().datetime().nullable().optional(),
  /** Site master switch for cloud zone alarms (read-only for tenant ops). */
  zoneAlarmsEnabled: z.boolean().optional(),
  /** Provider dev override: zone alarms for all tags without per-tag geofencing. */
  zoneAlarmsAllTagsEnabled: z.boolean().optional(),
});


export const SiteTagFleetListQuerySchema = z.object({
  tenantId: z.string().optional(),
  siteId: z.string().optional(),
  search: z.string().max(100).optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});


/** Tenant-scoped tag fleet list (tenantId comes from auth, not query). */
export const TenantSiteTagFleetListQuerySchema = SiteTagFleetListQuerySchema.omit({ tenantId: true });


export const TenantSiteTagFleetSiteParamsSchema = z.object({
  siteId: z.string().min(1).max(128),
});


export const SiteTagFleetListResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    sites: z.array(SiteTagFleetSummaryRowSchema),
    nextCursor: z.string().nullable().optional(),
    count: z.number().int().min(0),
  }),
});


export const TagFleetPositionSourceSchema = z.enum(['uwb', 'vendor']);


export const SiteTagFleetTagRowSchema = z.object({
  serialNumber: z.string(),
  online: z.boolean(),
  battery: z.number().nullable().optional(),
  lastSeenAt: z.string().nullable().optional(),
  inInventory: z.boolean(),
  sosActive: z.boolean().optional(),
  manufacturerName: z.string().nullable().optional(),
  deviceModel: z.string().nullable().optional(),
  firmwareVersion: z.string().nullable().optional(),
  /** Runtime telemetry origin — Cnapse Matrix UWB vs third-party vendor poll. */
  positionSource: TagFleetPositionSourceSchema.nullable().optional(),
  /** Set when positionSource is vendor (Kontakt, Pozyx, etc.). */
  vendorType: LocationIntegrationVendorTypeSchema.nullable().optional(),
  geofencingEnabled: z.boolean().optional(),
});


export const SiteTagFleetSiteParamsSchema = z.object({
  tenantId: z.string().min(1).max(128),
  siteId: z.string().min(1).max(128),
});


export const SiteTagFleetTagsListQuerySchema = z.object({
  search: z.string().max(100).optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});


export const SiteTagFleetTagsListResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    tenantId: z.string(),
    siteId: z.string(),
    tags: z.array(SiteTagFleetTagRowSchema),
    nextCursor: z.string().nullable().optional(),
    count: z.number().int().min(0),
  }),
});
// Inferred types
export type SiteTagFleetListQuery = z.infer<typeof SiteTagFleetListQuerySchema>;
export type SiteTagFleetListResponse = z.infer<typeof SiteTagFleetListResponseSchema>;
export type SiteTagFleetSiteParams = z.infer<typeof SiteTagFleetSiteParamsSchema>;
export type SiteTagFleetSummaryRow = z.infer<typeof SiteTagFleetSummaryRowSchema>;
export type SiteTagFleetTagRow = z.infer<typeof SiteTagFleetTagRowSchema>;
export type SiteTagFleetTagsListQuery = z.infer<typeof SiteTagFleetTagsListQuerySchema>;
export type SiteTagFleetTagsListResponse = z.infer<typeof SiteTagFleetTagsListResponseSchema>;
export type TagFleetPositionSource = z.infer<typeof TagFleetPositionSourceSchema>;
export type TenantSiteTagFleetListQuery = z.infer<typeof TenantSiteTagFleetListQuerySchema>;
export type TenantSiteTagFleetSiteParams = z.infer<typeof TenantSiteTagFleetSiteParamsSchema>;
export type VendorPollHeartbeatStatus = z.infer<typeof VendorPollHeartbeatStatusSchema>;
